import { createActionWithPayload, createReduxOperation, makeConstants } from "src/store/utils";
import AllPinsModel from "src/shared/models/AllPinsModel";
import { ThunkResult } from "src/shared/types";
import { PageTypes } from "src/shared/models/MetricOperations";
import { getTimeFromTimestamp, safeGet, TimeDifferenceGA } from "src/shared/utils";
import { FetchHomePayload, SearchPinPayload } from "./types";
import * as api from "src/api/home";
import { getCharts } from "src/api/common";
import { getServiceName, getXClientIdHeader, ServiceName } from "src/utils/getservicename";
import { devLogging } from "src/utils/logger";
import { IsTabHidden } from "src/shared/utils/customTab";
import { RunningAnobisMode } from "src/shared/config";
import { validateAccess } from "../login";
import { isL1, isL2, isPoc } from "src/utils/misRoleUtils";

// generate constants, actions and reducer
const {
  actions: [pinsRequest, pinsSuccess, pinsFailure],
  reducer: pinsReducer,
} = createReduxOperation("PINNED_METRICS");

const {
  actions: [searchRequest, searchSuccess, searchFailure, searchReset],
  reducer: searchReducer,
} = createReduxOperation("SEARCH_PINNED");

export { searchReducer, pinsReducer, searchReset };

export const homeParentConstants = makeConstants(["LOAD_MORE_PENDING", "FETCHING_HOME_DATA"]);

export const setLoadMorePending = createActionWithPayload<symbol, boolean>(
  homeParentConstants.get("LOAD_MORE_PENDING") as symbol
);

export const setChartsDataPending = createActionWithPayload<symbol, boolean>(
  homeParentConstants.get("FETCHING_HOME_DATA") as symbol
);

const noAccessErrorMessage =
  "Seems like you don't have access. Please reach out to IT Access support for providing proper access";

function fetchMetricsStaggeredStrategy(fetchedData, timeTab, filters, filterConfig, fresh, dispatch, getState) {
  const allMetrics = safeGet(getState(), "home.pinnedMetrics.metrics.payload", null);
  const allRequests: Promise<void>[] = [];

  for (let i = 0; i < fetchedData.length; i++) {
    const singleMetric = fetchedData[i];
    const singleChartRequest = AllPinsModel.getBulkChartRequest(
      timeTab,
      [singleMetric],
      PageTypes.L1,
      "pin",
      filters,
      filterConfig
    );

    const requestPromise = (async () => {
      const { response: chartResponse, error: chartError } = await getCharts({
        data: singleChartRequest,
        shouldFetchFreshData: fresh,
        timeTab,
      });

      if (chartResponse && chartResponse.data) {
        const formattedChart = AllPinsModel.formatCharts(chartResponse.data);
        const tempMetrics = {
          data: [singleMetric],
          hasMore: allMetrics.hasMore,
          pageSize: allMetrics.pageSize,
          pageNo: allMetrics.pageNo,
        };

        const fetchedPin = AllPinsModel.combineMetricsAndCharts(formattedChart, tempMetrics, PageTypes.L1);
        const fetchedPinWithData = AllPinsModel.calculationsFromCharts(fetchedPin, timeTab);

        const currentMetrics = safeGet(getState(), "home.pinnedMetrics.metrics.payload.data", []);
        const metricIndex = currentMetrics.findIndex((m) => m.id === singleMetric.id);

        if (metricIndex !== -1 && fetchedPinWithData.data.length > 0) {
          const updatedMetrics = [...currentMetrics];
          updatedMetrics[metricIndex] = fetchedPinWithData.data[0];

          dispatch(
            pinsSuccess({
              data: updatedMetrics,
              hasMore: allMetrics.hasMore,
              pageSize: allMetrics.pageSize,
              pageNo: allMetrics.pageNo,
              lastUpdatedTimeStamp: getTimeFromTimestamp(chartResponse.lastUpdatedTimeStamp),
            })
          );
        }
      }
    })();

    allRequests.push(requestPromise);
  }

  return Promise.all(allRequests);
}

function fetchMetricsBatchStrategy(
  fetchedData,
  timeTab,
  filters,
  filterConfig,
  fresh,
  dispatch,
  getState,
  pageNo,
  hasMore
) {
  const bulkChartRequest = AllPinsModel.getBulkChartRequest(
    timeTab,
    fetchedData,
    PageTypes.L1,
    "pin",
    filters,
    filterConfig
  );
  return getCharts({
    data: bulkChartRequest,
    shouldFetchFreshData: fresh,
    timeTab,
  }).then(({ response: chartsResponse, error: chartsError }) => {
    if (chartsResponse && chartsResponse.data) {
      const formattedChart = AllPinsModel.formatCharts(chartsResponse.data);
      const allMetrics = safeGet(getState(), "home.pinnedMetrics.metrics.payload", null);

      const fetchedPins = AllPinsModel.combineMetricsAndCharts(formattedChart, allMetrics, PageTypes.L1);
      const fetchedPinsWithData = AllPinsModel.calculationsFromCharts(fetchedPins, timeTab);

      if (getState().app.selectedTab !== timeTab) {
        dispatch(setChartsDataPending(false));
        return;
      }

      dispatch(
        pinsSuccess({
          data: fetchedPinsWithData.data,
          hasMore: !allMetrics.hasMore ? allMetrics.hasMore : hasMore,
          pageSize: fetchedPinsWithData.pageSize,
          pageNo,
          lastUpdatedTimeStamp: getTimeFromTimestamp(chartsResponse.lastUpdatedTimeStamp),
        })
      );
      if (timeTab == "Today") {
        const currentChartsData = chartsResponse.data.filter((item) => {
          return item["time_view"] == "currentChart";
        });
        TimeDifferenceGA(currentChartsData, "Pinned Metrics Page");
      }
    } else {
      dispatch(pinsFailure(chartsError));
    }
  });
  
}

/**
 * Thunk to fetch Pinned Metrics
 * Makes request to paginated api
 * Payload contains the page number to be fetched
 * 2 type of pending flags used,
 * One in case of fetch of page 1, requires loader to be shown
 * Another in case of loading subsequesnt pages
 */

const filterPinsBasedOnHiddenTabs = (response, timeTab) => {
  return {
    ...response,
    data: response.data.filter((pin) => !IsTabHidden(pin.isHidden, timeTab)),
  };
};

export function fetchHome({ pageNo, fresh, filters }: FetchHomePayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const userRoles = getState().app.userRoles.payload;
    if (!validateAccess(userRoles)) {
      dispatch(pinsFailure(noAccessErrorMessage));
      dispatch(setChartsDataPending(false));
      return;
    }
    if (pageNo === 1) {
      dispatch(pinsRequest());
    } else {
      dispatch(setLoadMorePending(true));
    }
    const timeTab = getState().app.selectedTab;
    const tabIndex =
      getServiceName() === ServiceName.ANOBIS_INDIA_2
        ? RunningAnobisMode.INDIA_2_MIS_TIME_TAB.indexOf(timeTab)
        : undefined;
    devLogging("tabIndex: ", tabIndex);
    dispatch(setChartsDataPending(true));
    const { response: pinsResponse, error: pinsError } = await api.getPins({ timeTab, pageNo, fresh, tabIndex });
    const metrics = safeGet(getState(), "home.pinnedMetrics.metrics.payload", null);
    const lastUpdatedTimeStamp = safeGet(getState(), "home.pinnedMetrics.metrics.payload.lastUpdatedTimeStamp", null);

    if (pinsResponse) {
      // instead of making API call for all metrics and then hiding. Don't make call for isHidden metrics
      const filteredPinsResponse = filterPinsBasedOnHiddenTabs(pinsResponse, timeTab);
      let pinData = AllPinsModel.createMetric(filteredPinsResponse);
      const hasMore = safeGet(pinData, "hasMore");
      let fetchedData = safeGet(pinData, "data", []);
      if (pageNo > 1) {
        const data = [...metrics.data];
        data.splice(data.length, 0, ...pinData.data);
        pinData = { ...metrics, data };
      }
      devLogging("redux store before pinsSuccess", pinData);

      dispatch(
        pinsSuccess({
          data: pinData.data,
          hasMore,
          pageSize: pinData.pageSize,
          pageNo,
          lastUpdatedTimeStamp,
        })
      );

      if (pageNo > 1) {
        dispatch(setLoadMorePending(false));
      }

      if (pinsResponse?.data?.length > 0) {
        const filterConfig = safeGet(getState(), "app.appConfig.payload", null);
        devLogging("get filter config from APi call", filterConfig);

        if (!userRoles.hasFullAccess) {
          devLogging("fetched data store ", fetchedData);
          fetchedData = fetchedData.map((pin) => {
            const existingCityFilter = (pin.filters || []).find(({ field }) => field === "city");
            if (existingCityFilter && existingCityFilter.value.length > 0) return pin;
            let modifiedCityFilter;
            if (userRoles.cities.length > 0) {
              modifiedCityFilter = existingCityFilter || { field: "city", value: [] };
              modifiedCityFilter.value = [...userRoles.cities];
            }
            return {
              ...pin,
              filters: [...pin.filters.filter(({ field }) => field !== "city"), modifiedCityFilter],
            };
          });
        }

        devLogging("final filters initial ", filters);
        // this section is completely for IMCOM app. we won't add any extra filter for isAdmin similar to Anobis here.
        if (getServiceName() === ServiceName.ANOBIS_IMCOM) {
          const modifiedPodFilter = [{ field: "store_id", value: [...userRoles.pods] }];
          const modifiedCityFilter = [{ field: "city", value: [...userRoles.cities] }];

          // Ensure `filters` is an array
          filters = filters || [];

          // Check if `filters` already contains a "store_id" filter
          if (!filters.some((filter) => filter.field === "store_id")) {
            // @ts-ignore
            filters = [...filters, ...modifiedPodFilter];
          }

          // Check if `filters` already contains a "city" filter
          if (!filters.some((filter) => filter.field === "city")) {
            // @ts-ignore
            filters = [...filters, ...modifiedCityFilter];
          }
          devLogging("final filters", filters);
        }

        // this section is only for india2mis app
        if (getServiceName() === ServiceName.ANOBIS_INDIA_2) {
          devLogging("get service name here ", getServiceName(), userRoles.restaurantIds, filters);
          // we only need restaurant_id filter here from user roles
          let modifiedRestaurantFilter: any[] = [];
          let modifiedCityFilter: any[] = [];

          if (isPoc(userRoles.misRole)) {
            modifiedRestaurantFilter = [{ field: "restaurant_id", value: [...userRoles.restaurantIds] }];
          } else if (isL1(userRoles.misRole)) {
            modifiedRestaurantFilter = [{ field: "restaurant_id", value: [...userRoles.restaurantIds] }];
          } else if (isL2(userRoles.misRole)) {
            modifiedCityFilter = [{ field: "city", value: [...userRoles.cities] }];
          } else {
            modifiedRestaurantFilter = [{ field: "restaurant_id", value: [...userRoles.restaurantIds] }];
          }
          // Ensure `filters` is an array
          filters = filters || [];

          // Check if `filters` already contains a "restaurant_id" filter
          if (!filters.some((filter) => filter.field === "restaurant_id") && !isL2(userRoles.misRole)) {
            // @ts-ignore
            filters = [...filters, ...modifiedRestaurantFilter];
          }

          // Check if `filters` already contains a "city" filter
          if (!filters.some((filter) => filter.field === "city")) {
            // @ts-ignore
            filters = [...filters, ...modifiedCityFilter];
          }

          devLogging("final filters for ANOBIS_INDIA_2", filters);
        }

        devLogging("final fetched data request ", fetchedData, timeTab);

        // Check if we're using IMCOM service
        if (getServiceName() === ServiceName.ANOBIS_IMCOM || getServiceName() === ServiceName.ANOBIS_INDIA_2) {
          await fetchMetricsStaggeredStrategy(fetchedData, timeTab, filters, filterConfig, fresh, dispatch, getState);
        } else {
          await fetchMetricsBatchStrategy(
            fetchedData,
            timeTab,
            filters,
            filterConfig,
            fresh,
            dispatch,
            getState,
            pageNo,
            hasMore
          );
        }
      }
    } else {
      dispatch(pinsFailure(pinsError));
    }
    dispatch(setChartsDataPending(false));
  };
}

export function searchPin({ text }: SearchPinPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(searchRequest());
    if (!text) {
      dispatch(searchSuccess([]));
      return;
    }
    const { response, error } = await api.searchPins(text);
    if (response) {
      const pins = AllPinsModel.createMetric({ data: response });

      dispatch(searchSuccess(pins.data));
      const timeTab = getState().app.selectedTab;
      const filterConfig = safeGet(getState(), "app.appConfig.payload", null);

      interface AdhocFilter {
        field: string;
        value: string[];
      }

      const { hasFullAccess, cities = [] } = getState().app.userRoles.payload;
      const userRoleAdhocFilters: AdhocFilter[] = hasFullAccess ? [] : [{ field: "city", value: cities }];

      const bulkChartRequest = AllPinsModel.getBulkChartRequest(
        timeTab,
        safeGet(pins, "data", pins),
        PageTypes.L1Search,
        "metric", // even search  in home, will search for all org metrics
        userRoleAdhocFilters,
        filterConfig
      );

      const { response: chartsResponse } = await getCharts({
        data: bulkChartRequest,
        timeTab,
      });
      if (chartsResponse && chartsResponse.data) {
        const formattedChart = AllPinsModel.formatCharts(chartsResponse.data);
        const fetchedPins = AllPinsModel.combineMetricsAndCharts(formattedChart, pins, PageTypes.L1Search);
        const fetchedPinsWithData = AllPinsModel.calculationsFromCharts({ data: fetchedPins }, timeTab);
        dispatch(searchSuccess(fetchedPinsWithData.data));
        if (timeTab === "Today") {
          const currentChartsData = chartsResponse.data.filter((item) => {
            return item["time_view"] === "currentChart";
          });
          TimeDifferenceGA(currentChartsData, "Search Pinned Metrics Page");
        }
      }
    } else {
      dispatch(searchFailure(error));
    }
  };
}
